// src/states/notification.state.ts
import { NotificationService } from '@/api/notification';
import { INotification, INotificationData, WSNotificationMessage } from '@/types/notification';
import { create } from 'zustand';

interface NotificationState {
  // New notification system
  notifications: INotification[];
  unreadCount: number;
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchNotifications: (tenantId: number, page?: number, pageSize?: number) => Promise<void>;
  fetchUnreadCount: (tenantId: number) => Promise<void>;
  markAsRead: (tenantId: number, notificationId: string) => Promise<void>;
  markAllAsRead: (tenantId: number) => Promise<void>;
  addRealtimeNotification: (notification: WSNotificationMessage['notification']) => void;
  clearNotifications: () => void;

  // Legacy support
  notificationList: INotificationData[];
  notificationCount: number;
  setNotificationList: (list: INotificationData[]) => void;
  setNotifictionCount: (count: number) => void;
  fetchLegacyNotifications: (email: string) => Promise<void>;
}

const useNotificationState = create<NotificationState>((set, get) => ({
  // New notification system state
  notifications: [],
  unreadCount: 0,
  total: 0,
  page: 1,
  pageSize: 20,
  isLoading: false,
  error: null,

  // Legacy state
  notificationList: [],
  notificationCount: 0,

  // New actions
  fetchNotifications: async (tenantId: number, page = 1, pageSize = 20) => {
    set({ isLoading: true, error: null });
    try {
      const data = await NotificationService.fetchNotifications(tenantId, page, pageSize);
      set({
        notifications: data.notifications,
        unreadCount: data.unread_count,
        total: data.total,
        page: data.page,
        pageSize: data.page_size,
        isLoading: false,
      });
    } catch (error) {
      set({ error: 'Failed to fetch notifications', isLoading: false });
      console.error('Failed to fetch notifications:', error);
    }
  },

  fetchUnreadCount: async (tenantId: number) => {
    try {
      const count = await NotificationService.fetchUnreadCount(tenantId);
      set({ unreadCount: count });
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  },

  markAsRead: async (tenantId: number, notificationId: string) => {
    try {
      await NotificationService.markAsRead(tenantId, notificationId);
      // Update local state
      set(state => ({
        notifications: state.notifications.map(n =>
          n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  },

  markAllAsRead: async (tenantId: number) => {
    try {
      await NotificationService.markAllAsRead(tenantId);
      // Update local state
      set(state => ({
        notifications: state.notifications.map(n => ({
          ...n,
          is_read: true,
          read_at: n.read_at || new Date().toISOString(),
        })),
        unreadCount: 0,
      }));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  },

  addRealtimeNotification: (notification: WSNotificationMessage['notification']) => {
    const newNotification: INotification = {
      id: notification.id,
      type: notification.type,
      type_label: getTypeLabel(notification.type),
      data: notification.data,
      is_read: false,
      read_at: null,
      created_at: notification.created_at,
    };

    set(state => ({
      notifications: [newNotification, ...state.notifications],
      unreadCount: state.unreadCount + 1,
      total: state.total + 1,
    }));
  },

  clearNotifications: () => {
    set({
      notifications: [],
      unreadCount: 0,
      total: 0,
      page: 1,
    });
  },

  // Legacy actions
  setNotificationList: (list: INotificationData[]) => set({ notificationList: list }),
  setNotifictionCount: (count: number) => set({ notificationCount: count }),
  fetchLegacyNotifications: async (email: string) => {
    const notifications = await NotificationService.fetchLegacyNotifications({
      page: 0,
      limit: 100,
      isRead: false,
      userId: email,
    });

    if (notifications) {
      set({ notificationList: notifications.content, notificationCount: notifications.totalElements });
    } else {
      set({ notificationList: [], notificationCount: 0 });
    }
  },
}));

// Helper function to get type label
function getTypeLabel(type: string): INotification['type_label'] {
  switch (type) {
    case 'App\\Notifications\\DiscussionMessage':
      return 'Discussion Message';
    case 'App\\Notifications\\ACKConfirmation':
      return 'ACK Confirmation';
    case 'App\\Notifications\\PICAssignment':
      return 'PIC Assignment';
    default:
      return 'Discussion Message';
  }
}

export default useNotificationState;
